import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LocationDetail } from '../models/Location';
import axios from 'axios';
import { Coordinates, parsePublicLocations } from '../helpers/locationBrowse';

export interface LocationExpansion {
    expandedCity: string | null;
    expandedLocation: string | null;
}

interface ILocationContext {
    locations: LocationDetail[];
    locationsStatus: 'loading' | 'ready' | 'error';
    reloadLocations: () => void;
    uniqueCity: number;
    userPosition: Coordinates | null;
    setUserPosition: (position: Coordinates | null) => void;
    expansionHistory: React.MutableRefObject<Map<string, LocationExpansion>>;
}

export const LocationContext = React.createContext<ILocationContext>({
    locations: [],
    locationsStatus: 'loading',
    reloadLocations: () => { },
    uniqueCity: 0,
    userPosition: null,
    setUserPosition: () => { },
    expansionHistory: { current: new Map() },
});

interface ILocationProvider {
    children: ReactNode;
}

export const LocationProvider: React.FC<ILocationProvider> = ({ children }) => {
    const [locations, setLocations] = useState<LocationDetail[]>([]);
    const [locationsStatus, setLocationsStatus] = useState<ILocationContext['locationsStatus']>('loading');
    const [loadAttempt, setLoadAttempt] = useState(0);
    const reloadLocations = useCallback(() => setLoadAttempt(attempt => attempt + 1), []);
    const [uniqueCity, setUniqueCity] = useState<number>(0)
    const [userPosition, setUserPosition] = useState<Coordinates | null>(null);
    const expansionHistory = useRef(new Map<string, LocationExpansion>());
    const value = useMemo(() => ({ locations, locationsStatus, reloadLocations, uniqueCity, userPosition, setUserPosition, expansionHistory }),
        [locations, locationsStatus, reloadLocations, uniqueCity, userPosition]);
    useEffect(() => {
        const controller = new AbortController();
        setLocationsStatus('loading');
        axios.get(process.env.REACT_APP_DATABASE_API_URL + "gettagdatabase.php?locbrowser=2", {
            signal: controller.signal, timeout: 20000,
        }).then(response => {
            if (controller.signal.aborted) return;
            if (!response.data || typeof response.data !== 'object' || Array.isArray(response.data)) {
                throw new Error('Invalid location database response');
            }
            const locationsWithId = parsePublicLocations(response.data);

            setLocations(locationsWithId);
            setUniqueCity(countUniqueCities(locationsWithId));
            setLocationsStatus('ready');
        }).catch(() => {
            if (!controller.signal.aborted) setLocationsStatus('error');
        });
        return () => controller.abort();
    }, [loadAttempt]);

    const countUniqueCities = (data: LocationDetail[]): number => {
        const uniqueCities = new Set<string>();

        data.forEach((item) => {
            if (item.city) uniqueCities.add(item.city);
        });

        return uniqueCities.size;
    }

    return (
        <LocationContext.Provider value={value}>
            {children}
        </LocationContext.Provider>
    );
};
